import React, { useEffect, useState } from 'react';
import block from 'bem-cn';
import { useSelector } from 'react-redux';

import ProviderIcon from 'components/ProviderIcon';
import SVG from 'components/SVG';
import CheckBox from 'components/CheckBox';
import Button from 'components/Button';
import upSVG from 'features/reports/img/up.svg';
import downSVG from 'features/reports/img/down.svg';
import filterSVG from 'features/sportHistory/img/filter.svg';
import { providerScheduleMetrics } from 'features/reports/data';
import convertToAmericanFormat from 'shared/helpers/convertToAmericanFormat';
import arrowSVG from 'shared/img/arrow.svg';
import providerArrowSVG from './img/arrow.svg';

import ActiveUsersLoading from 'features/reports/view/desktop/Pages/FinancialReports/General/ActiveUsersLoading/ActiveUsersLoading';

import './TableRow.scss';

const b = block('table-providers-row');
const metricFields = [
  { key: 'betCount', currency: false },
  { key: 'betAmount', currency: true },
  { key: 'winAmount', currency: true },
  { key: 'incomeAmount', currency: true },
  { key: 'rtp', suffix: '%' },
  { key: 'activeUsers', currency: false },
];

const RankingTable = ({ title, headers, rows }) => (
  <div className={b('ranking')}>
    <h3 className={b('ranking-title')}>{title}</h3>
    <div className={b('ranking-head')}>
      {headers.map(header => <span key={header.key}>{header.label}</span>)}
    </div>
    <div className={b('ranking-body')}>
      {rows.length ? rows.map((row, index) => (
        <div className={b('ranking-row')} key={`${row.number}_${index}`}>
          {headers.map(header => <span key={header.key}>{row[header.key]}</span>)}
        </div>
      )) : <div className={b('ranking-empty')}>—</div>}
    </div>
  </div>
);

const TableRow = ({
  item,
  activeUsers,
  isActiveUsersLoading,
  isToggleDisabled,
  diff,
  isExpanded,
  onToggle,
  selectedMetrics,
  onMetricsApply,
  onScheduleOpen,
  metricError,
  ranks,
  withDiff,
  currency,
}) => {
  const locale = useSelector(state => state.locale.locale);
  const activeUsersValue = activeUsers?.providers.find(provider => provider.providerKey === item.providerKey)?.activeUsers;
  const activeUsersDiff = activeUsers?.providersDiff?.[item.providerKey];
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [draftMetrics, setDraftMetrics] = useState(selectedMetrics);

  useEffect(() => {
    setDraftMetrics(selectedMetrics);
  }, [selectedMetrics]);

  useEffect(() => {
    if (!isExpanded) setIsFiltersOpen(false);
  }, [isExpanded]);

  const handleMetricToggle = metric => {
    setDraftMetrics(current =>
      current.includes(metric) ? current.filter(item => item !== metric) : [...current, metric],
    );
  };

  const handleMetricsApply = () => {
    onMetricsApply(draftMetrics);
    if (draftMetrics.length) setIsFiltersOpen(false);
  };

  return (
    <article className={b({ expanded: isExpanded })}>
      <div className={b('tr')}>
        <button
          type="button"
          className={b('provider')}
          onClick={onToggle}
          disabled={isToggleDisabled}
          aria-expanded={isExpanded}>
          <SVG className={b('arrow')} svgProps={{ svg: providerArrowSVG }} />
          <ProviderIcon className={b('item-icon')} name={item.provider.toLowerCase()} />
          <span>{item.provider}</span>
        </button>
        {metricFields.map(field => {
          const isActiveUsers = field.key === 'activeUsers';
          const metricDiff = isActiveUsers ? activeUsersDiff : diff;
          const percent = Number(metricDiff?.[`${field.key}Percent`] || 0);
          const value = isActiveUsers ? activeUsersValue : item[field.key] ?? 0;
          return (
            <div key={field.key} className={b('metric')}>
              <span className={b('metric-value')}>
                {isActiveUsers && isActiveUsersLoading ? (
                  <ActiveUsersLoading />
                ) : value == null ? '—' : convertToAmericanFormat(String(value))}{field.suffix || ''}
                {field.currency && <small>{currency}</small>}
              </span>
              {withDiff && (!isActiveUsers || (!isActiveUsersLoading && activeUsersDiff)) && (
                <span className={b('metric-diff')}>
                  {percent !== 0 && (
                    <SVG className={b('metric-diff-icon')} svgProps={{ svg: percent > 0 ? upSVG : downSVG }} />
                  )}
                  <span className={b('metric-diff-percent', { positive: percent > 0, neutral: percent === 0 })}>
                    {`${percent > 0 ? '+' : ''}${percent}%`}
                  </span>
                  <span className={b('metric-diff-value')}>
                    {convertToAmericanFormat(String(metricDiff?.[field.key] ?? 0))}{field.suffix || ''}
                  </span>
                </span>
              )}
            </div>
          );
        })}
      </div>

      {isExpanded && (
        <div className={b('details')}>
          <div className={b('controls')}>
            <div className={b('schedule-button')}>
              <Button callBack={onScheduleOpen} text={locale.showSchedule} />
            </div>
            <div className={b('filters')}>
              <span className={b('filters-title')}>{locale.filters}</span>
              <button
                type="button"
                className={b('filters-trigger')}
                onClick={() => setIsFiltersOpen(current => !current)}
                aria-expanded={isFiltersOpen}>
                <span className={b('filters-trigger-content')}>
                  <SVG className={b('filters-icon')} svgProps={{ svg: filterSVG }} />
                  {locale.filters}
                </span>
                <SVG className={b('filters-arrow')} svgProps={{ svg: arrowSVG }} />
              </button>

              {isFiltersOpen && (
                <div className={b('filters-menu')}>
                  {providerScheduleMetrics.map(metric => (
                    <label className={b('filters-item')} key={metric.key}>
                      <CheckBox
                        name={metric.key}
                        checked={draftMetrics.includes(metric.key)}
                        onChange={() => handleMetricToggle(metric.key)}
                      />
                      <span>{locale[metric.label]}</span>
                    </label>
                  ))}
                  <div className={b('filters-apply')}>
                    <Button callBack={handleMetricsApply} text={locale.acceptFilter} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {metricError && <div className={b('metric-error')}>{locale.selectAtLeastOneMetric}</div>}

          <div className={b('rankings')}>
            <RankingTable
              title={locale.top10PlayersByNetwin}
              headers={[
                { key: 'number', label: locale.rankColumn },
                { key: 'username', label: locale.user },
                { key: 'netwin', label: locale.netwin },
              ]}
              rows={ranks.topByNetwin || []}
            />
            <RankingTable
              title={locale.top5BiggestWins}
              headers={[
                { key: 'number', label: locale.rankColumn },
                { key: 'game', label: locale.game },
                { key: 'win', label: locale.win },
                { key: 'date', label: locale.date },
              ]}
              rows={ranks.biggestWins || []}
            />
          </div>
        </div>
      )}
    </article>
  );
};

export default TableRow;
